import { useGetWriteoffWorklistQuery, useGetWriteoffApprovalsByUserQuery } from '../../../services/WriteoffService';
import { WriteoffWorklistType, WRITEOFF_WORKLIST_TYPES } from '../../../services/WriteoffService/types/writeoffWorklistTypes';
import { WriteoffStatusSummary } from '../components/dashboard/WriteoffDashboardCard';
import { STATUS_CONFIGS } from '../components/dashboard/dashboardConstants';
import { useGetMyProfileQuery } from '../../../services/ProfileService';

type WriteoffStatus = 'Draft' | 'InApproval' | 'MyApprovals' | 'Postable' | 'Rejected';

export const useWriteoffDashboardData = (company: string, center: string) => {
  const shouldSkip = !company || !center;
  
  // Get current user's profile for My Approvals query
  const { data: profile } = useGetMyProfileQuery();
  const userName = profile?.name || '';

  const draftQuery = useGetWriteoffWorklistQuery(
    { type: 'Draft', center, company },
    { skip: shouldSkip }
  );
  const inApprovalQuery = useGetWriteoffWorklistQuery(
    { type: 'InApproval', center, company },
    { skip: shouldSkip }
  );
  const postableQuery = useGetWriteoffWorklistQuery(
    { type: 'Postable', center, company },
    { skip: shouldSkip }
  );
  const rejectedQuery = useGetWriteoffWorklistQuery(
    { type: 'Rejected', center, company },
    { skip: shouldSkip }
  );
  
  // Fetch My Approvals for current user
  const myApprovalsQuery = useGetWriteoffApprovalsByUserQuery(
    { center, company, userName },
    { skip: shouldSkip || !userName }
  );

  const queries: Record<WriteoffWorklistType, typeof draftQuery> = {
    Draft: draftQuery,
    InApproval: inApprovalQuery,
    Postable: postableQuery,
    Rejected: rejectedQuery
  };

  const ALL_STATUSES: WriteoffStatus[] = ['Draft', 'InApproval', 'MyApprovals', 'Postable', 'Rejected'];

  const summaries: WriteoffStatusSummary[] = ALL_STATUSES.map(type => {
    const config = STATUS_CONFIGS[type];

    if (type === 'MyApprovals') {
      const myApprovalsData = myApprovalsQuery.data || [];
      const count = myApprovalsData.length;
      const totalAmount = myApprovalsData.reduce((sum, writeoff) => sum + (writeoff.amount || 0), 0);
      
      return {
        type,
        count,
        totalAmount,
        icon: config.icon,
        color: config.color,
        bgColor: config.bgColor,
        displayName: config.displayName,
        description: config.description
      };
    }

    const query = queries[type as WriteoffWorklistType];
    const data = query.data || [];
    const count = data.length;
    const totalAmount = data.reduce((sum, writeoff) => sum + (writeoff.amount || 0), 0);

    return {
      type,
      count,
      totalAmount,
      icon: config.icon,
      color: config.color,
      bgColor: config.bgColor,
      displayName: config.displayName,
      description: config.description
    };
  });

  const isLoading = Object.values(queries).some(query => query.isLoading) || myApprovalsQuery.isLoading;
  const hasError = Object.values(queries).some(query => query.error) || !!myApprovalsQuery.error;

  // Exclude MyApprovals from totals to avoid double-counting (they're already in InApproval)
  const summariesForTotals = summaries.filter(s => s.type !== 'MyApprovals');
  const totalCount = summariesForTotals.reduce((sum, summary) => sum + summary.count, 0);
  const totalAmount = summariesForTotals.reduce((sum, summary) => sum + summary.totalAmount, 0);

  return {
    summaries,
    isLoading,
    hasError,
    totalCount,
    totalAmount
  };
};
